import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Divider,
  Stack,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormLabel,
  RadioGroup,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import * as Yup from 'yup';
import { Form } from '../../components/Form/Form';
import { FileUpload } from '../../components/FileUpload/FileUpload';
import { DocumentPreview } from '../../components/DocumentPreview/DocumentPreview';
import { SekurantenDialog } from '../../components/SekurantenDialog/SekurantenDialog';
import * as projectsAPI from '../../services/projectsAPI';
import { useSnackbar } from 'notistack';
import { Project } from '../../types/api';
import { dailyReportsAPI, regieReportsAPI, sekurantenAPI, sekurantenProjekteAPI } from '../../services/api';
import AddIcon from '@mui/icons-material/Add';
import { SekurantenProjekt } from '../../types/api';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { API_BASE_URL } from '../../config';

const validationSchema = Yup.object({
  name: Yup.string().required('Projektname ist erforderlich'),
  description: Yup.string().required('Beschreibung ist erforderlich'),
  client_name: Yup.string().required('Kunde ist erforderlich'),
  client_contact: Yup.string(),
  site_address: Yup.string().required('Projektadresse ist erforderlich'),
  status: Yup.string().required('Status ist erforderlich'),
  start_date: Yup.date().required('Startdatum ist erforderlich'),
  end_date: Yup.date().nullable(),
  contract_amount: Yup.number()
    .min(0, 'Muss größer oder gleich 0 sein')
    .required('Auftragssumme ist erforderlich'),
  material_share: Yup.number()
    .min(0, 'Muss größer oder gleich 0 sein')
    .required('Materialanteil ist erforderlich'),
  planned_hours: Yup.number()
    .min(0, 'Muss größer oder gleich 0 sein')
    .required('Geplante Stunden sind erforderlich'),
});

interface FormValues {
  name: string;
  description: string;
  client_name: string;
  client_contact?: string;
  site_address: string;
  status: 'active' | 'completed' | 'archived';
  start_date: Date;
  end_date: Date | null;
  contract_amount: number;
  material_share: number;
  planned_hours: number;
}

interface ProjectFormProps {
  viewMode?: boolean;
}

export const ProjectForm: React.FC<ProjectFormProps> = ({ viewMode = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  // Rollen-basierte Berechtigungen für Projekte
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management';
  const isBauleiter = user?.role === 'bauleiter';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin, Management & Bauleiter können alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Monteure haben keinen Zugriff auf das Formular
  const canSeeRestricted = isForeman || isInstaller;
  
  // Projekt-Berechtigungen:
  const canCreateProjects = canSeeAll; // Admin, Management, Bauleiter können erstellen
  const canEditAllFields = isAdmin || isManagement; // Admin & Management können alle Felder bearbeiten
  const canEditContractAmount = canSeeAll; // Bauleiter kann nur Auftragssumme bearbeiten
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<File[]>([]);
  const [existingDocuments, setExistingDocuments] = useState<projectsAPI.ProjectDocument[]>([]);
  const [initialValues, setInitialValues] = useState<FormValues>({
    name: '',
    description: '',
    client_name: '',
    client_contact: '',
    site_address: '',
    status: 'active',
    start_date: new Date(),
    end_date: null,
    contract_amount: 0,
    material_share: 0,
    planned_hours: 0,
  });
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dailyReportPhotos, setDailyReportPhotos] = useState<string[]>([]);
  const [sekurantenPhotos, setSekurantenPhotos] = useState<string[]>([]);
  const [sekuranten, setSekuranten] = useState<any[]>([]);
  const [sekurantenDialogOpen, setSekurantenDialogOpen] = useState(false);
  const [sekurantenProjekte, setSekurantenProjekte] = useState<SekurantenProjekt[]>([]);

  useEffect(() => {
    // Berechtigung prüfen
    if (id && !canSeeAll) {
      enqueueSnackbar('Keine Berechtigung zum Bearbeiten von Projekten', { variant: 'error' });
      navigate('/projects');
      return;
    }
    if (!id && !canCreateProjects) {
      enqueueSnackbar('Keine Berechtigung zum Erstellen von Projekten', { variant: 'error' });
      navigate('/projects');
      return;
    }

    const fetchProject = async () => {
      if (!id) return;

      try {
        const project = await projectsAPI.getProject(Number(id));
        setInitialValues({
          name: project.name,
          description: project.description,
          client_name: project.client_name,
          client_contact: project.client_contact || '',
          site_address: project.site_address,
          status: project.status,
          start_date: new Date(project.start_date),
          end_date: project.end_date ? new Date(project.end_date) : null,
          contract_amount: project.contract_amount,
          material_share: project.material_share,
          planned_hours: project.planned_hours,
        });
        
        // Load existing documents
        const docs = await projectsAPI.getProjectDocuments(Number(id));
        setExistingDocuments(docs);
      } catch (error) {
        console.error('Error fetching project:', error);
        enqueueSnackbar('Fehler beim Laden des Projekts', { variant: 'error' });
      }
    };

    fetchProject();
  }, [id, enqueueSnackbar]);

  // Separate function for fetching photos that can be called multiple times
  const fetchRelatedPhotos = async () => {
    if (!id) return;
    setDashboardLoading(true);
    try {
      // Regieberichte Fotos
      const dailyReportsResp = await dailyReportsAPI.getReports();
      const dailyPhotos = dailyReportsResp.items
        .filter(r => r.project_id === Number(id))
        .flatMap(r => r.photos || []);
      setDailyReportPhotos(dailyPhotos.map(photo =>
        photo.file_path.startsWith('http')
          ? photo.file_path
          : `${API_BASE_URL.replace('/api', '')}/${photo.file_path.replace(/^\/+/, '')}`
      ));
      // Sekuranten Fotos
      const sekurantenResp = await sekurantenAPI.getSekuranten();
      const sekPhotos = sekurantenResp
        .filter(s => s.sekuranten_projekt_id === Number(id))
        .flatMap(s => s.photos || []);
      setSekurantenPhotos(sekPhotos);
    } catch (e) {
      // ignore for dashboard
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    fetchRelatedPhotos();
    
    // Auto-refresh photos every 10 seconds when viewing a project
    const interval = setInterval(() => {
      fetchRelatedPhotos();
    }, 10000);

    return () => clearInterval(interval);
  }, [id]);

  // Add a function to always fetch Sekuranten for the current project
  const fetchSekuranten = async () => {
    if (id) {
      const sekurantenList = await sekurantenAPI.getSekuranten(Number(id));
      setSekuranten(sekurantenList);
    }
  };

  useEffect(() => {
    fetchSekuranten();
  }, [id]);

  useEffect(() => {
    if (id) {
      sekurantenProjekteAPI.getProjekte(Number(id)).then(setSekurantenProjekte);
    }
  }, [id]);

  const handleDeleteDocument = async (documentId: number) => {
    if (!id) return;
    
    try {
      await projectsAPI.deleteDocument(Number(id), documentId);
      setExistingDocuments(docs => docs.filter(doc => doc.id !== documentId));
      enqueueSnackbar('Dokument erfolgreich gelöscht', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting document:', error);
      enqueueSnackbar('Fehler beim Löschen des Dokuments', { variant: 'error' });
    }
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setLoading(true);
      const projectData = {
        name: values.name,
        description: values.description,
        client_name: values.client_name,
        client_contact: values.client_contact,
        site_address: values.site_address,
        status: values.status,
        start_date: values.start_date.toISOString().split('T')[0],
        end_date: values.end_date ? values.end_date.toISOString().split('T')[0] : null,
        contract_amount: Number(values.contract_amount),
        material_share: Number(values.material_share),
        planned_hours: Number(values.planned_hours),
      };

      let projectId: number;

      if (id) {
        const updatedProject = await projectsAPI.updateProject(Number(id), projectData);
        projectId = Number(id);
        enqueueSnackbar('Projekt erfolgreich aktualisiert', { variant: 'success' });
      } else {
        const createdProject = await projectsAPI.createProject(projectData);
        projectId = createdProject.id;
        enqueueSnackbar('Projekt erfolgreich erstellt', { variant: 'success' });
      }

      // Upload documents if any
      if (documents.length > 0) {
        try {
          for (const document of documents) {
            await projectsAPI.uploadDocument(projectId, document);
          }
          enqueueSnackbar(`${documents.length} Dokumente erfolgreich hochgeladen`, { variant: 'success' });
        } catch (error) {
          console.error('Error uploading documents:', error);
          enqueueSnackbar('Fehler beim Hochladen der Dokumente', { variant: 'warning' });
        }
      }

      navigate('/projects');
    } catch (error) {
      console.error('Error submitting project:', error);
      enqueueSnackbar('Fehler beim Speichern des Projekts', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSekurant = () => {
    setSekurantenDialogOpen(true);
  };

  const handleSekurantenSuccess = async () => {
    await fetchSekuranten();
    enqueueSnackbar('Sekurant erfolgreich hinzugefügt', { variant: 'success' });
  };

  const fields = [
    {
      name: 'name',
      label: 'Projektname',
      type: 'text' as const,
      required: true,
    },
    {
      name: 'description',
      label: 'Beschreibung',
      type: 'textarea' as const,
      required: true,
      rows: 4,
    },
    {
      name: 'client_name',
      label: 'Kunde',
      type: 'text' as const,
      required: true,
    },
    {
      name: 'client_contact',
      label: 'Ansprechpartner',
      type: 'text' as const,
      required: false,
    },
    {
      name: 'site_address',
      label: 'Projektadresse',
      type: 'text' as const,
      required: true,
    },
    {
      name: 'contract_amount',
      label: 'Auftragssumme (€)',
      type: 'number' as const,
      required: true,
    },
    {
      name: 'material_share',
      label: 'Materialanteil (€)',
      type: 'number' as const,
      required: true,
    },
    {
      name: 'planned_hours',
      label: 'Geplante Gesamtstunden',
      type: 'number' as const,
      required: true,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select' as const,
      options: [
        { label: 'Aktiv', value: 'active' },
        { label: 'Abgeschlossen', value: 'completed' },
        { label: 'Archiviert', value: 'archived' },
      ],
      required: true,
    },
    {
      name: 'start_date',
      label: 'Startdatum',
      type: 'date' as const,
      required: true,
    },
    {
      name: 'end_date',
      label: 'Enddatum',
      type: 'date' as const,
      required: false,
    },
  ];

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        {id ? (viewMode ? 'Projekt anzeigen' : 'Projekt bearbeiten') : 'Neues Projekt'}
      </Typography>

      {/* Dashboard Panel */}
      {id && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Projektübersicht</Typography>
            <Grid>
              <Grid>
                <strong>Kunde:</strong> {initialValues.client_name}
              </Grid>
              <Grid>
                <strong>Ansprechpartner:</strong> {initialValues.client_contact}
              </Grid>
              <Grid>
                <strong>Adresse der Baustelle:</strong> {initialValues.site_address}
              </Grid>
              <Grid>
                <strong>Status:</strong>{' '}
                <Chip
                  label={
                    initialValues.status === 'active'
                      ? 'Laufend'
                      : initialValues.status === 'completed'
                      ? 'Abgeschlossen'
                      : 'Archiviert'
                  }
                  color={
                    initialValues.status === 'active'
                      ? 'success'
                      : initialValues.status === 'completed'
                      ? 'info'
                      : 'default'
                  }
                  size="small"
                />
              </Grid>
            </Grid>
            {/* Projektbilder Galerie */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1">Projektbilder</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {existingDocuments.filter(doc => doc.content_type?.startsWith('image/')).map(doc => (
                  <img
                    key={doc.id}
                    src={projectsAPI.getDocumentViewUrl(Number(id), doc.id)}
                    alt={doc.original_filename}
                    style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8 }}
                  />
                ))}
                {existingDocuments.filter(doc => doc.content_type?.startsWith('image/')).length === 0 && (
                  <Typography variant="body2" color="text.secondary">Keine Bilder vorhanden</Typography>
                )}
              </Box>
            </Box>
            {/* Fotos aus Regieberichten */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="subtitle1">Fotos aus Regieberichten</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={fetchRelatedPhotos}
                  disabled={dashboardLoading}
                >
                  Aktualisieren
                </Button>
              </Box>
              {dashboardLoading ? <CircularProgress size={20} /> : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {dailyReportPhotos.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`Regiebericht Foto ${idx + 1}`}
                      style={{ width: 100, height: 75, objectFit: 'cover', borderRadius: 6 }}
                    />
                  ))}
                  {dailyReportPhotos.length === 0 && (
                    <Typography variant="body2" color="text.secondary">Keine Fotos vorhanden</Typography>
                  )}
                </Box>
              )}
            </Box>
            {/* Regieberichte Tabelle */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1">Regieberichte</Typography>
              <RegieReportsTable projectId={Number(id)} />
            </Box>
            {/* Tagesberichte Tabelle */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1">Tagesberichte</Typography>
              <TagesberichteTable projectId={Number(id)} />
            </Box>
            {/* Sekurantenprojekte Tabelle */}
            {id && (
            <Box mt={4}>
                <Typography variant="h6" gutterBottom>
                  Sekurantenprojekte zu diesem Projekt
                </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Objektname</TableCell>
                        <TableCell>Erstellt am</TableCell>
                        <TableCell align="right" sx={{ width: 180 }}>Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                      {sekurantenProjekte.map((sp) => (
                        <TableRow key={sp.id}>
                          <TableCell>{sp.name}</TableCell>
                          <TableCell>{sp.objektname}</TableCell>
                          <TableCell>{sp.created_at}</TableCell>
                        <TableCell align="right" sx={{ width: 180 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              sx={{ minWidth: 90 }}
                              onClick={() => navigate(`/sekuranten-projekte/${sp.id}`)}
                            >
                              Anzeigen
                            </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                      {sekurantenProjekte.length === 0 && (
                      <TableRow>
                          <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                              Keine Sekurantenprojekte vorhanden
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Grid>
            <Grid>
              <Form
                fields={fields.map(f => ({ ...f, disabled: viewMode }))}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                loading={loading}
                onCancel={() => navigate('/projects')}
                submitButtonText={viewMode ? '' : undefined}
              />
            </Grid>
            
            {id && existingDocuments.length > 0 && (
              <Grid>
                <Typography variant="subtitle1" gutterBottom>
                  Vorhandene Dokumente
                </Typography>
                <Box sx={{ mb: 2 }}>
                  {existingDocuments.map((doc) => (
                    <DocumentPreview
                      key={doc.id}
                      document={doc}
                      projectId={Number(id)}
                      onDelete={!viewMode ? () => handleDeleteDocument(doc.id) : undefined}
                    />
                  ))}
                </Box>
              </Grid>
            )}
            
            {!viewMode && (
            <Grid>
              <Typography variant="subtitle1" gutterBottom>
                {id ? 'Neue Dokumente hinzufügen' : 'Dokumente'}
              </Typography>
              <FileUpload
                files={documents}
                onFilesChange={setDocuments}
                accept=".pdf,.docx"
                multiple
                label="Dokumente hochladen (PDF, DOCX)"
              />
            </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Sekuranten Dialog */}
      {id && !viewMode && (
        <SekurantenDialog
          open={sekurantenDialogOpen}
          onClose={() => setSekurantenDialogOpen(false)}
          onSave={handleSekurantenSuccess}
          sekurantenProjektId={Number(id)}
        />
      )}
    </Box>
  );
};

// Hilfskomponente für die Regieberichte-Tabelle
const RegieReportsTable: React.FC<{ projectId: number }> = ({ projectId }) => {
  const [reports, setReports] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();

  React.useEffect(() => {
    let mounted = true;
    regieReportsAPI.getReports().then(resp => {
      if (mounted) {
        setReports((resp.items || []).filter(r => r.project_id === projectId));
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, [projectId]);

  if (loading) return <CircularProgress size={20} />;
  if (reports.length === 0) return <Typography variant="body2" color="text.secondary">Keine Regieberichte vorhanden</Typography>;
  return (
    <TableContainer component={Paper} sx={{ mt: 1 }}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 120 }}>Nummer</TableCell>
            <TableCell sx={{ width: 220 }}>Titel</TableCell>
            <TableCell sx={{ width: 140 }}>Datum</TableCell>
            <TableCell sx={{ width: 180 }} align="right">Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reports.map(report => (
            <TableRow key={report.id}>
              <TableCell sx={{ width: 120 }}>{report.rapport_nr || report.id}</TableCell>
              <TableCell sx={{ width: 220 }}>{report.title || `Regiebericht #${report.id}`}</TableCell>
              <TableCell sx={{ width: 140 }}>{report.date ? new Date(report.date).toLocaleDateString() : '-'}</TableCell>
              <TableCell sx={{ width: 180 }} align="right">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  sx={{ minWidth: 90 }}
                  onClick={() => navigate(`/regie-reports/${report.id}`)}
                >
                  Anzeigen
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Hilfskomponente für die Tagesberichte-Tabelle
const TagesberichteTable: React.FC<{ projectId: number }> = ({ projectId }) => {
  const [reports, setReports] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();

  React.useEffect(() => {
    let mounted = true;
    dailyReportsAPI.getReports().then(resp => {
      if (mounted) {
        setReports((resp.items || []).filter(r => r.project_id === projectId));
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, [projectId]);

  if (loading) return <CircularProgress size={20} />;
  if (reports.length === 0) return <Typography variant="body2" color="text.secondary">Keine Tagesberichte vorhanden</Typography>;
  return (
    <TableContainer component={Paper} sx={{ mt: 1 }}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 120 }}>Nummer</TableCell>
            <TableCell sx={{ width: 220 }}>Titel</TableCell>
            <TableCell sx={{ width: 140 }}>Datum</TableCell>
            <TableCell sx={{ width: 180 }} align="right">Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reports.map(report => (
            <TableRow key={report.id}>
              <TableCell sx={{ width: 120 }}>{report.rapport_nr || report.id}</TableCell>
              <TableCell sx={{ width: 220 }}>{report.title || `Tagesbericht #${report.id}`}</TableCell>
              <TableCell sx={{ width: 140 }}>{report.date ? new Date(report.date).toLocaleDateString() : '-'}</TableCell>
              <TableCell sx={{ width: 180 }} align="right">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  sx={{ minWidth: 90 }}
                  onClick={() => navigate(`/daily-reports/${report.id}`)}
                >
                  Anzeigen
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}; 